import { TypedEventEmitter } from './event-emitter.js';
import { DEFAULT_GESTURE_CONFIG } from './constants.js';
import { RollDetector } from './roll-detector.js';
import { ThrowDetector } from './throw-detector.js';
import { OddballCC } from './types.js';
import type {
  GestureDetectorConfig,
  GestureDetectorEventMap,
  OddballEvent,
  CCUpdateEvent,
  TapEvent,
  ShakeEvent,
  TwistEvent,
  AbruptStopEvent,
  SustainedShakeEvent,
  SustainedTwistEvent,
  IdleEvent,
  GestureEvent,
} from './types.js';

interface NoteRecord {
  velocity: number;
  timestamp: number;
}

export class GestureDetector extends TypedEventEmitter<GestureDetectorEventMap> {
  private config: GestureDetectorConfig;

  /** Roll sub-detector (public for direct subscriptions) */
  readonly roll: RollDetector;
  /** Throw sub-detector (public for direct subscriptions) */
  readonly throw: ThrowDetector;

  // CC state needed by coordinator
  private moveSmoothed = 0;
  private energy = 0;

  // Abrupt stop tracking
  private motionStartTime = 0;
  private lastEnergyHigh = 0;

  // Sustained note tracking
  private shakeWindow: NoteRecord[] = [];
  private twistWindow: NoteRecord[] = [];
  private lastSustainedShakeEmit = -Infinity;
  private lastSustainedTwistEmit = -Infinity;

  // Idle tracking
  private lastActivityTime = 0;
  private idleEmitted = false;

  constructor(config?: Partial<GestureDetectorConfig>) {
    super();
    this.config = { ...DEFAULT_GESTURE_CONFIG, ...config };

    this.roll = new RollDetector(this.config);
    this.throw = new ThrowDetector(this.config);

    // Coordination: throw ends active roll (THE FIX)
    this.throw.on('throwStart', (evt) => {
      if (this.roll.rolling) this.roll.forceEnd(evt.timestamp);
      this.emitGesture(evt);
    });

    // Forward all sub-detector events → 'gesture' catch-all
    this.roll.on('rollStart', (e) => this.emitGesture(e));
    this.roll.on('rolling', (e) => this.emitGesture(e));
    this.roll.on('rollDirectionChange', (e) => this.emitGesture(e));
    this.roll.on('rollEnd', (e) => this.emitGesture(e));
    // throwStart is already forwarded above
    this.throw.on('airborne', (e) => this.emitGesture(e));
    this.throw.on('caught', (e) => this.emitGesture(e));
    this.throw.on('landed', (e) => this.emitGesture(e));
  }

  /** Snapshot of internal roll detection state for debug visualization */
  getRollDiagnostics(now?: number): import('./types.js').RollDiagnostics {
    return this.roll.getDiagnostics(now);
  }

  feed(event: OddballEvent): void {
    this.lastActivityTime = event.timestamp;
    this.idleEmitted = false;

    switch (event.type) {
      case 'cc':
        this.handleCC(event);
        break;
      case 'tap':
        this.handleTap(event);
        break;
      case 'shake':
        this.handleShake(event);
        break;
      case 'twist':
        this.handleTwist(event);
        break;
      case 'noteOff':
        break;
    }
  }

  /** Check for idle — call periodically or after feeding events */
  checkIdle(now: number): void {
    if (
      !this.idleEmitted &&
      this.lastActivityTime > 0 &&
      now - this.lastActivityTime >= this.config.idleTimeoutMs &&
      !this.roll.rolling &&
      !this.throw.airborne
    ) {
      this.idleEmitted = true;
      const event: IdleEvent = {
        type: 'idle',
        timestamp: now,
        idleDuration: now - this.lastActivityTime,
      };
      this.emitGesture(event);
    }
  }

  private handleCC(event: CCUpdateEvent): void {
    const { cc, value, timestamp } = event;

    // Route to sub-detectors
    this.roll.handleCC(cc, value, timestamp);
    this.throw.handleCC(cc, value, timestamp);

    // Coordinator-level CC handling
    switch (cc) {
      case OddballCC.MoveSmoothed: {
        const prev = this.moveSmoothed;
        this.moveSmoothed = value;

        if (value > 0 && prev === 0) {
          this.motionStartTime = timestamp;
        }

        if (value === 0 && prev > 0) {
          this.checkAbruptStop(timestamp);
        }
        break;
      }

      case OddballCC.Energy:
        this.energy = value;
        if (value === 1) {
          this.lastEnergyHigh = timestamp;
        }
        break;
    }
  }

  private handleTap(event: TapEvent): void {
    this.roll.handleTap(event.timestamp);
    this.throw.handleTap(event.velocity, event.timestamp);
  }

  private handleShake(event: ShakeEvent): void {
    this.shakeWindow.push({ velocity: event.velocity, timestamp: event.timestamp });
    this.pruneWindow(this.shakeWindow, event.timestamp);
    this.checkSustainedShake(event.timestamp);
  }

  private handleTwist(event: TwistEvent): void {
    this.twistWindow.push({ velocity: event.velocity, timestamp: event.timestamp });
    this.pruneWindow(this.twistWindow, event.timestamp);
    this.checkSustainedTwist(event.timestamp);
  }

  // ── Abrupt Stop ────────────────────────────────────────────────────

  private checkAbruptStop(timestamp: number): void {
    const motionDuration = timestamp - this.motionStartTime;
    if (
      motionDuration >= this.config.abruptStopMinMotionMs &&
      this.lastEnergyHigh > 0 &&
      timestamp - this.lastEnergyHigh < 500
    ) {
      const event: AbruptStopEvent = {
        type: 'abruptStop',
        timestamp,
        priorEnergyLevel: this.energy,
        motionDuration,
      };
      this.emitGesture(event);
    }
  }

  // ── Sustained Notes ────────────────────────────────────────────────

  private pruneWindow(window: NoteRecord[], now: number): void {
    while (window.length > 0 && now - window[0].timestamp > this.config.sustainedWindowMs) {
      window.shift();
    }
  }

  private checkSustainedShake(timestamp: number): void {
    if (this.shakeWindow.length < this.config.sustainedMinEvents) return;
    if (timestamp - this.lastSustainedShakeEmit < this.config.sustainedWindowMs) return;

    this.lastSustainedShakeEmit = timestamp;
    const velocities = this.shakeWindow.map(r => r.velocity);
    const sum = velocities.reduce((a, b) => a + b, 0);

    const event: SustainedShakeEvent = {
      type: 'sustainedShake',
      startTime: this.shakeWindow[0].timestamp,
      duration: timestamp - this.shakeWindow[0].timestamp,
      averageIntensity: sum / velocities.length,
      peakIntensity: Math.max(...velocities),
      burstCount: velocities.length,
    };
    this.emitGesture(event);
  }

  private checkSustainedTwist(timestamp: number): void {
    if (this.twistWindow.length < this.config.sustainedMinEvents) return;
    if (timestamp - this.lastSustainedTwistEmit < this.config.sustainedWindowMs) return;

    this.lastSustainedTwistEmit = timestamp;
    const velocities = this.twistWindow.map(r => r.velocity);
    const sum = velocities.reduce((a, b) => a + b, 0);

    const event: SustainedTwistEvent = {
      type: 'sustainedTwist',
      startTime: this.twistWindow[0].timestamp,
      duration: timestamp - this.twistWindow[0].timestamp,
      averageIntensity: sum / velocities.length,
      peakIntensity: Math.max(...velocities),
      eventCount: velocities.length,
    };
    this.emitGesture(event);
  }

  // ── Emit helper ────────────────────────────────────────────────────

  private emitGesture(event: GestureEvent): void {
    this.emit(event.type as keyof GestureDetectorEventMap, event as any);
    this.emit('gesture', event);
  }
}
